import { useState, useEffect } from 'react';
import SearchPanel from './SearchPanel';
import { KoobEmptyState, KoobLoader, KoobSearchBar } from '@koob/ui';
import SearchResults from './SearchResults';
import {
  SEARCH_MIN_LENGTH,
  SEARCH_DEBOUNCE,
  SEARCH_DEFAULT_TYPES,
} from '../constants';
import { useAsync } from 'react-async';
import { useDebounce } from 'use-debounce';
import search from '../api';
import { toast } from 'react-toastify';
import { isEqual } from 'lodash';

export default function SearchInput({
  types = SEARCH_DEFAULT_TYPES,
  fromOrganizationId,
  scopeDmcId,
  value,
  isMarketPlaceSource = false,
  onChange,
  searchedCountries = [],
  locale,
  rounded,
  /** Fill the parent width instead of the fixed w-60. */
  block = false,
}) {
  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebounce(query, SEARCH_DEBOUNCE);
  const [isFocused, setIsFocused] = useState(false);
  const [showPanel, setShowPanel] = useState(false);

  // KD-3565 — the bar displays `value?.title ?? query`, so a value cleared by
  // the parent (selecting another day card resets the trip location filter)
  // used to fall back on the last searched title, which stayed on screen.
  //
  // Adjusted during render rather than in an effect: React re-runs this
  // component before committing, so the stale title is never painted. Compared
  // on the selected id, never on `value` itself — between two selections
  // `value` stays empty, so typing never triggers it.
  const selectedId = value?.id ?? null;
  const [lastSelectedId, setLastSelectedId] = useState(selectedId);

  if (selectedId !== lastSelectedId) {
    setLastSelectedId(selectedId);
    setQuery(value?.title ?? '');
  }

  const formattedQuery = debouncedQuery?.trim();

  const isQueryValid = formattedQuery?.length >= SEARCH_MIN_LENGTH;

  // Surface every failure, not just 503 — a swallowed timeout/5xx used to look
  // identical to an empty result set, so high-latency users saw "No results"
  // instead of a real error they could retry (KD-3515).
  const onSearchError = (err) => {
    toast(
      err?.response?.status === 503
        ? 'Search is temporarily unavailable, please try again.'
        : 'Search failed, please try again.',
      { type: 'error' },
    );
  };

  const { data, isLoading, error } = useAsync({
    promiseFn: search,
    skip: !isQueryValid,
    query: formattedQuery,
    isMarketPlaceSource: isMarketPlaceSource,
    types,
    fromOrganizationId,
    scopeDmcId,
    searchedCountries,
    locale,
    onReject: onSearchError,
    watchFn: (prev, next) => {
      return prev.query !== next.query || !isEqual(prev.types, next.types);
    },
  });

  // Page 1 comes from useAsync (re-runs on query/types change); further pages
  // are appended here and reset whenever page 1 changes.
  const [morePages, setMorePages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  useEffect(() => {
    setMorePages([]);
    setCurrentPage(1);
  }, [data]);

  const results = data ? [data.data ?? [], ...morePages].flat() : undefined;
  const hasMore = currentPage < (data?.meta?.lastPage ?? 1);

  const loadMore = async () => {
    const nextPage = currentPage + 1;
    setIsLoadingMore(true);
    try {
      const res = await search({
        query: formattedQuery,
        types,
        fromOrganizationId,
        scopeDmcId,
        isMarketPlaceSource,
        searchedCountries,
        locale,
        page: nextPage,
      });
      setMorePages((pages) => [...pages, res.data ?? []]);
      setCurrentPage(nextPage);
    } catch (err) {
      onSearchError(err);
    } finally {
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    if (isFocused) {
      setShowPanel(true);
    }
  }, [isFocused]);

  const displayComponent = () => {
    if (isLoading) {
      return (
        <KoobEmptyState
          variant="plain"
          icon={<KoobLoader size={24} />}
          description="Loading..."
        />
      );
    }

    if (!isQueryValid) {
      return (
        <KoobEmptyState
          variant="plain"
          icon={<i className="fa-regular fa-magnifying-glass text-2xl" />}
          description={`Type to search ${types?.join(', ')}...`}
        />
      );
    }

    if (error) {
      return (
        <KoobEmptyState
          variant="plain"
          icon={<i className="fal fa-triangle-exclamation text-2xl" />}
          description="Search failed. Please try again."
        />
      );
    }

    if (results?.length === 0) {
      return (
        <KoobEmptyState
          variant="plain"
          icon={<i className="fal fa-xmark text-2xl" />}
          description="No results found"
        />
      );
    }

    return (
      <SearchResults
        query={formattedQuery}
        results={results}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        onLoadMore={loadMore}
        onSelect={(value) => {
          if (!value) {
            onChange('');
            setQuery('');
            return;
          }
          onChange({
            ...value,
            kind: value.type,
          });
          setQuery(value?.title);
        }}
      />
    );
  };

  return (
    <div className={block ? 'relative w-full' : 'relative w-60'}>
      <div
        className={[
          'relative',
          showPanel && 'z-popover',
          value && 'cursor-pointer',
        ]
          .filter(Boolean)
          .join(' ')}
        // A selected value locks the (disabled) input; clicking anywhere on
        // the field clears it — same contract as the legacy SearchInputField.
        onClick={() => {
          if (value) {
            setQuery('');
            onChange(null);
          }
          setShowPanel(true);
        }}
      >
        <KoobSearchBar
          rounded={rounded}
          placeholder={`Search ${types.join(', ')}`}
          value={value?.title ?? query ?? ''}
          onChange={(v) => {
            setQuery(v);
            if (!v) {
              onChange(null);
            }
          }}
          iconRight={value ? <i className="fa-regular fa-xmark" /> : undefined}
          // Turns the clear icon into a button (cursor-pointer + hover state).
          onIconRightClick={
            value
              ? () => {
                  setQuery('');
                  onChange(null);
                }
              : undefined
          }
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          data-testid="search-input"
        />
      </div>

      <div className="w-screen max-w-[640px]">
        <SearchPanel
          isOpen={showPanel}
          onClose={() => {
            setShowPanel(false);
            setIsFocused(false);
          }}
        >
          {displayComponent()}
        </SearchPanel>
      </div>
    </div>
  );
}
